import React, { useEffect } from 'react';
import { useAuth } from './auth';
import { navigate, useLocation } from './router';
import Dashboard, { Section } from './pages/Dashboard';
import Landing from './pages/Landing';
import Login from './pages/Login';
import ExtensionPage, { ExtensionConnected } from './pages/ExtensionPage';
import { Contact, NotFound, PricingPage, Privacy, Refunds, Terms } from './pages/Legal';
import './App.css';
import './site.css';

const APP_SECTIONS: Record<string, Section> = {
  '/app': 'today',
  '/app/practice': 'practice',
  '/app/settings': 'settings',
};

const TITLES: Record<string, string> = {
  '/': 'CodeReviser · Spaced repetition for LeetCode',
  '/login': 'Sign in · CodeReviser',
  '/signup': 'Create account · CodeReviser',
  '/pricing': 'Pricing · CodeReviser',
  '/extension': 'Chrome extension · CodeReviser',
  '/terms': 'Terms · CodeReviser',
  '/privacy': 'Privacy · CodeReviser',
  '/refunds': 'Refunds · CodeReviser',
  '/contact': 'Contact · CodeReviser',
};

function FullPageSpinner() {
  return (
    <div className="center-state">
      <div className="spinner spinner-lg" />
    </div>
  );
}

// Pages that need an account redirect to sign-in and come back afterwards.
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { me } = useAuth();
  const { path } = useLocation();
  const authed = me?.authenticated;

  useEffect(() => {
    if (me && !authed) navigate(`/login?next=${encodeURIComponent(path + window.location.search)}`, { replace: true });
  }, [me, authed, path]);

  return authed ? <>{children}</> : <FullPageSpinner />;
}

export default function App() {
  const { ready } = useAuth();
  const { path } = useLocation();
  const route = path.length > 1 ? path.replace(/\/+$/, '') : path;

  useEffect(() => {
    document.title = TITLES[route] ?? (route.startsWith('/app') ? 'Dashboard · CodeReviser' : 'CodeReviser');
  }, [route]);

  if (!ready) return <FullPageSpinner />;

  if (APP_SECTIONS[route]) {
    return <RequireAuth><Dashboard section={APP_SECTIONS[route]} /></RequireAuth>;
  }

  switch (route) {
    case '/': return <Landing />;
    case '/login': return <Login key="login" mode="login" />;
    case '/signup': return <Login key="signup" mode="signup" />;
    case '/pricing': return <PricingPage />;
    case '/extension': return <ExtensionPage />;
    case '/extension/connected': return <RequireAuth><ExtensionConnected /></RequireAuth>;
    case '/terms': return <Terms />;
    case '/privacy': return <Privacy />;
    case '/refunds': return <Refunds />;
    case '/contact': return <Contact />;
    default: return <NotFound />;
  }
}
